import { Injectable } from '@angular/core';
import { UserDefaultParams as DefaultParams } from '@app/models/user-default-params.class';
import { User, UserCreate, UserWithRelationships } from '@app/models/user.interface';
import {
  UsersService,
  UsersWithRelationshipsService,
  RpcCreateUserService,
  RpcGetUserRolesService,
  RpcSetUserRolesPostRequest,
  RpcSetUserRolesService,
  RpcAuthenticateParticipantPostRequest,
  RpcCreateParticipantPostRequest,
  RpcVerifyTokenService,
  RpcVerifyTokenNotExpiredService,
  RpcBanUserService,
  RpcUnbanUserService,
  RpcSetUserPasswordService,
} from '@lib/core';
import { Observable, map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserHelperService {

  constructor(
    private service: UsersService,
    private usersWithRelationshipsService: UsersWithRelationshipsService,
    private createRpc: RpcCreateUserService,
    private getRolesRpc: RpcGetUserRolesService,
    private setRolesRpc: RpcSetUserRolesService,
    private verifyTokenRpc: RpcVerifyTokenService,
    private verifyTokenNotExpired: RpcVerifyTokenNotExpiredService,
    private banUserRpc: RpcBanUserService,
    private unbanUserRpc: RpcUnbanUserService,
    private setUserPasswordRpc: RpcSetUserPasswordService
  ) { }

  getAll(): Observable<UserWithRelationships[]> {
    let params = new DefaultParams();
    return this.usersWithRelationshipsService.usersWithRelationshipsGet(params)
      .pipe(map(x => <UserWithRelationships[]>x));
  }

  search(status?: string | null): Observable<UserWithRelationships[]> {
    let params = new DefaultParams();

    if (status === 'active') {
      params.isBanned = 'eq.false';
    } else if (status === 'disabled') {
      params.isBanned = 'eq.true';
    }
    // If status is 'all' or null, don't set isBanned parameter

    return this.usersWithRelationshipsService.usersWithRelationshipsGet(params)
      .pipe(map(x => <UserWithRelationships[]>x));
  }
  getById(id: string): Observable<UserWithRelationships> {
    let params = <any>new DefaultParams();
    params.id = 'eq.' + id;
    return this.usersWithRelationshipsService.usersWithRelationshipsGet(params)
      .pipe(map(x => <UserWithRelationships>x[0]));
  }
  create(input: RpcCreateParticipantPostRequest) {
    return this.createRpc.rpcCreateUserPost({args: input}, 'response')
    .pipe(map(resp => {
      let body = resp.body;
      return <string>body;
    }));
  }
  update(id: string, input: UserCreate) {
    return this.service.usersPatch({
      id: 'eq.'+id,
      users: <any>input
    }, 'response')
    .pipe(map(response => response.ok));
  }
  getUserRoles(input: RpcAuthenticateParticipantPostRequest){
    return this.getRolesRpc.rpcGetUserRolesPost({args: input}, 'response')
    .pipe(map(resp => {
      let body = resp.body;
      return <any>body;
    }));
  }
  setUserRoles(input: RpcSetUserRolesPostRequest){
    return this.setRolesRpc.rpcSetUserRolesPost({args: input}, 'response')
    .pipe(map(resp => {
      let body = resp.body;
      return <string>body;
    }));
  }

  verifyToken(token: string): Observable<boolean> {
    return this.verifyTokenRpc.rpcVerifyTokenPost({ args: {
      token_in: token,
    }}).pipe(map(x => <boolean>x));
  }

  tokenNotExpired(token: string, token_type: string): Observable<boolean> {
    return this.verifyTokenNotExpired.rpcVerifyTokenNotExpiredPost({ args: {
      token_in: token,
      token_type: token_type
    }}).pipe(map(x => <boolean>x));
  }

  disableUser(userId: string): Observable<void> {
    return this.banUserRpc.rpcBanUserPost({
      args: {
        target_user_id: userId,
        reason: 'Disabled by admin'
      }
    }, 'response').pipe(map(() => void 0));
  }

  enableUser(userId: string): Observable<void> {
    return this.unbanUserRpc.rpcUnbanUserPost({
      args: {
        target_user_id: userId
      }
    }, 'response').pipe(map(() => void 0));
  }

  setUserPassword(userId: string): Observable<string> {
    return this.setUserPasswordRpc.rpcSetUserPasswordPost({
      args: {
        target_user_id: userId
      }
    }, 'response').pipe(map(resp => <string>resp.body));
  }
}
